import React, { useEffect, useState } from 'react'
import {
  Box,
  Badge,
  Button,
  Card,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  Flex,
  Table
} from '@chakra-ui/react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiCheck, FiInfo } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'
import { EstimatesTable } from '../components/EstimatesTable'
import { useOnboarding } from '../contexts/OnboardingContext'

export const PricingPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { generated_assets, basic_info, onboardingMode } = useOnboarding()
  
  // Check if coming from onboarding
  const isFromOnboarding = location.state?.fromOnboarding || 
                          (generated_assets?.carousel_candidates?.length > 0 || generated_assets?.selected_ad || generated_assets?.ad_candidates?.length > 0)

  // Get the correct path prefix based on onboarding mode
  const getOnboardingPath = (path: string) => {
    const mode = onboardingMode || 'service'
    const prefix = mode === 'product' ? '/for-products/onboarding' : '/for-services/onboarding'
    return `${prefix}${path}`
  }

  const handleApply = () => {
    if (isFromOnboarding) {
      // If in onboarding flow, should go to checkout/payment, not restart
      // For now, keep navigation but this should be updated when checkout is implemented
      navigate(getOnboardingPath('/basic-setup'))
    } else {
      navigate(getOnboardingPath('/basic-setup'))
    }
  }
  const handleBackToLandingPage = () => navigate(getOnboardingPath('/landing-page'))
  
  // Get selected carousel template (first one if available)
  const selectedCarousel = generated_assets?.carousel_candidates?.[0]
  const selectedCtaType = generated_assets?.landing_page?.cta_type

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column">
      <SpotlightHeader />

      <Box flex={1}>
        {/* Hero */}
        <Box
          bgGradient="linear(to-br, blue.50, brand.50)"
          py={{ base: 16, md: 24 }}
          position="relative"
          overflow="hidden"
        >
          {/* Background decoration */}
          <Box
            position="absolute"
            top="-20%"
            right="-10%"
            w="600px"
            h="600px"
            borderRadius="full"
            bg="brand.100"
            opacity={0.3}
            filter="blur(100px)"
          />
          <Box
            position="absolute"
            bottom="-20%"
            left="-10%"
            w="600px"
            h="600px"
            borderRadius="full"
            bg="blue.100"
            opacity={0.3}
            filter="blur(100px)"
          />

          <Container maxW="7xl" position="relative" zIndex={1}>
            {isFromOnboarding && (
              <Flex justify="flex-start" mb={6}>
                <Button variant="ghost" leftIcon={<Icon as={FiArrowLeft} />} onClick={handleBackToLandingPage}>
                  Back (edit landing page)
                </Button>
              </Flex>
            )}
            <VStack align="center" gap={8} textAlign="center" maxW="4xl" mx="auto">
              {isFromOnboarding && (
                <Box
                  bg="green.50"
                  borderWidth="1px"
                  borderColor="green.200"
                  borderRadius="full"
                  px={4}
                  py={2}
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                >
                  <Icon as={FiCheckCircle} color="green.600" />
                  <Text fontSize="sm" fontWeight="semibold" color="green.700">
                    Your carousel is ready — select a package to continue.
                  </Text>
                </Box>
              )}
              <Badge
                colorPalette="brand"
                size="lg"
                px={4}
                py={2}
                borderRadius="full"
                variant="subtle"
                border="1px solid"
                borderColor="brand.200"
              >
                Pricing & Packs
              </Badge>
              <Heading
                size={{ base: '3xl', md: '4xl', lg: '5xl' }}
                color="gray.900"
                lineHeight="shorter"
                fontWeight="extrabold"
                letterSpacing="tight"
              >
                {isFromOnboarding ? 'Select your visibility package' : 'Simple, transparent visibility.'}
              </Heading>
              <Text fontSize="xl" color="gray.600" maxW="3xl" lineHeight="tall">
                {isFromOnboarding
                  ? "You’re one step away. Choose a visibility pack and we’ll guide you through the remaining setup before anything goes live."
                  : "We handle all the marketing complexity. You just choose your visibility level and pay when you activate. No subscriptions, no commitments, no ad managers to learn."}
              </Text>

              {isFromOnboarding && (
                <Card.Root borderColor="brand.200" bg="brand.50" w="full" borderWidth="2px">
                  <Card.Body p={{ base: 5, md: 6 }}>
                    <VStack gap={4} align="stretch" textAlign="left">
                      <Heading size="md" color="gray.900">
                        What you’re selecting (placeholder)
                      </Heading>
                      <Text fontSize="sm" color="gray.700">
                        Review what you've created, then select a visibility pack to activate your carousel ads.
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={2}>Your Carousel</Text>
                          <Text fontSize="sm" color="gray.800" mb={1}>
                            <Text as="span" fontWeight="semibold">Template:</Text> {selectedCarousel?.template_name || 'Transformation Story'}
                          </Text>
                          <Text fontSize="sm" color="gray.800" mb={1}>
                            <Text as="span" fontWeight="semibold">Slides:</Text> {selectedCarousel?.slides?.length || 4}-{selectedCarousel?.slides?.length || 7} slides
                          </Text>
                          <Text fontSize="sm" color="gray.800">
                            <Text as="span" fontWeight="semibold">Message:</Text> {selectedCarousel?.primary_message || 'Transformation'}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={2}>Your Landing Page</Text>
                          <Text fontSize="sm" color="gray.800" mb={1}>
                            <Text as="span" fontWeight="semibold">CTA Type:</Text> {selectedCtaType === 'ask_question' ? 'Ask a Question' : 'Book a Call'}
                          </Text>
                          <Text fontSize="sm" color="gray.800" mb={1}>
                            <Text as="span" fontWeight="semibold">Destination:</Text> Spotlight page
                          </Text>
                          <Text fontSize="sm" color="gray.800">
                            <Text as="span" fontWeight="semibold">Status:</Text> Ready to launch
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={2}>After Payment</Text>
                          <Text fontSize="sm" color="gray.700" mb={1}>
                            • Confirm your booking preferences
                          </Text>
                          <Text fontSize="sm" color="gray.700" mb={1}>
                            • Ads launch automatically on Meta
                          </Text>
                          <Text fontSize="sm" color="gray.700">
                            • Track results in your dashboard
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              )}
            </VStack>
          </Container>
        </Box>

        {/* Pricing Structure - Impression Packs */}
        <Container maxW="7xl" py={{ base: 16, md: 24 }}>
          <VStack gap={16}>
            {/* Impression Packs */}
            <Box w="full">
              <Heading size="2xl" textAlign="center" mb={8}>Choose Your Visibility Pack</Heading>
              <Text fontSize="lg" color="gray.600" textAlign="center" mb={12} maxW="2xl" mx="auto">
                Pay per pack activation. No subscriptions. Activate packs whenever you need more visibility.
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
                {[
                  {
                    impressions: 10000,
                    price: 199,
                    popular: false,
                    description: "Perfect for testing or filling immediate openings"
                  },
                  {
                    impressions: 25000,
                    price: 399,
                    popular: true,
                    description: "Most popular for consistent client flow"
                  },
                  {
                    impressions: 50000,
                    price: 699,
                    popular: false,
                    description: "For established practices ready to scale"
                  },
                  {
                    impressions: 100000,
                    price: 1199,
                    popular: false,
                    description: "Maximum visibility for aggressive growth"
                  }
                ].map((pack, idx) => (
                  <Card.Root
                    key={idx}
                    borderWidth={pack.popular ? "2px" : "1px"}
                    borderColor={pack.popular ? "brand.500" : "gray.200"}
                    bg="white"
                    borderRadius="2xl"
                    shadow={pack.popular ? "xl" : "sm"}
                    position="relative"
                    transform={pack.popular ? { lg: 'scale(1.05)' } : undefined}
                    zIndex={pack.popular ? 10 : 1}
                  >
                    {pack.popular && (
                      <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bg="brand.500"
                        color="white"
                        py={2}
                        textAlign="center"
                        fontWeight="bold"
                        fontSize="sm"
                        textTransform="uppercase"
                        letterSpacing="wide"
                        borderTopRadius="2xl"
                      >
                        Most Popular
                      </Box>
                    )}
                    <Card.Body p={8} pt={pack.popular ? 12 : 8}>
                      <VStack align="start" gap={6}>
                        <Box>
                          <Heading size="xl" color="gray.900" mb={2}>
                            {pack.impressions.toLocaleString()} Impressions
                          </Heading>
                          <HStack align="baseline">
                            <Heading size="4xl" color={pack.popular ? "brand.600" : "gray.900"}>
                              ${pack.price}
                            </Heading>
                            <Text color="gray.500">per pack</Text>
                          </HStack>
                          <Text color="gray.600" mt={3} fontSize="sm">
                            {pack.description}
                          </Text>
                        </Box>
                        <VStack align="start" gap={3} w="full" pt={4} borderTop="1px solid" borderColor="gray.100">
                          <HStack gap={2}>
                            <Icon as={FiCheckCircle} color="brand.500" />
                            <Text fontSize="sm" color="gray.600">
                              <strong>{pack.impressions.toLocaleString()} impressions</strong> delivered via Meta
                            </Text>
                          </HStack>
                          <HStack gap={2}>
                            <Icon as={FiCheckCircle} color="brand.500" />
                            <Text fontSize="sm" color="gray.600">Done-for-you ad creatives</Text>
                          </HStack>
                          <HStack gap={2}>
                            <Icon as={FiCheckCircle} color="brand.500" />
                            <Text fontSize="sm" color="gray.600">Simple analytics dashboard</Text>
                          </HStack>
                          <HStack gap={2}>
                            <Icon as={FiCheckCircle} color="brand.500" />
                            <Text fontSize="sm" color="gray.600">No subscription required</Text>
                          </HStack>
                        </VStack>
                        <Button
                          colorPalette={pack.popular ? "brand" : "gray"}
                          variant={pack.popular ? "solid" : "outline"}
                          size="lg"
                          w="full"
                          onClick={() => {
                            // TODO: When checkout is implemented, route to checkout with pack ID
                            // For now, show message or route appropriately
                            if (isFromOnboarding) {
                              // Should go to checkout, but keeping current behavior for now
                              console.log('Selecting pack:', pack.impressions, 'Price:', pack.price)
                              // Navigate to checkout when implemented
                              handleApply()
                            } else {
                              handleApply()
                            }
                          }}
                          fontWeight="bold"
                        >
                          {isFromOnboarding ? 'Continue to Checkout' : 'Select this pack'}
                        </Button>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                ))}
              </SimpleGrid>
            </Box>

            {/* Note about pricing */}
            <Box bg="gray.50" p={6} borderRadius="lg" w="full" maxW="3xl" mx="auto" textAlign="center">
              <HStack justify="center" gap={2} mb={2}>
                <Icon as={FiInfo} color="gray.500" />
                <Text fontWeight="bold" color="gray.700">How It Works</Text>
              </HStack>
              <VStack align="start" gap={2} textAlign="left" maxW="md" mx="auto">
                <Text fontSize="sm" color="gray.600">
                • <strong>Pay per pack:</strong> Activate visibility packs only when you need them
                </Text>
                <Text fontSize="sm" color="gray.600">
                • <strong>No subscriptions:</strong> No monthly fees, no commitments
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>

        {/* ROI / Estimates Section */}
        <Box bg="gray.50" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <VStack gap={12} align="stretch">
              {/* Emotional ROI Stories */}
              <Box bg="white" p={8} borderRadius="2xl" shadow="sm" borderWidth="1px" borderColor="gray.200">
                <VStack align="start" gap={6}>
                  <Badge colorPalette="green" size="md">Real Stories</Badge>
                  <Heading size="2xl" color="gray.900">Real stories from practitioners</Heading>
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={6} w="full">
                    <Box>
                      <Text fontSize="md" color="gray.700" lineHeight="tall" fontStyle="italic">
                        "I activated my first pack and had 3 consultation calls within a week"
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="md" color="gray.700" lineHeight="tall" fontStyle="italic">
                        "Finally, a marketing system that doesn't require me to be a marketer"
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="md" color="gray.700" lineHeight="tall" fontStyle="italic">
                        "My practice went from struggling to find clients to a steady stream of inquiries in 6 weeks"
                      </Text>
                    </Box>
                  </SimpleGrid>
                </VStack>
              </Box>

              <SimpleGrid columns={{ base: 1, lg: 2 }} gap={16} alignItems="center">
                <VStack align="start" gap={6}>
                  <Badge colorPalette="green" size="md">ROI Calculator</Badge>
                  <Heading size="3xl" color="gray.900">What can you expect?</Heading>
                  <Text fontSize="xl" color="gray.600" lineHeight="tall">
                    Marketing isn't magic—it's math. Based on our data across wellness niches, here is what typical visibility packs generate in terms of potential client opportunities.
                  </Text>
                  <VStack align="start" gap={4} pt={4}>
                    <HStack align="start">
                      <Icon as={FiCheckCircle} color="green.500" mt={1} />
                      <Text color="gray.700"><strong>Conservative Estimates:</strong> We prefer to under-promise and over-deliver.</Text>
                    </HStack>
                    <HStack align="start">
                      <Icon as={FiCheckCircle} color="green.500" mt={1} />
                      <Text color="gray.700"><strong>Quality over Quantity:</strong> We target high-intent leads, not just random clicks.</Text>
                    </HStack>
                  </VStack>
                </VStack>

                <Box>
                  <EstimatesTable />
                </Box>
              </SimpleGrid>
            </VStack>
          </Container>
        </Box>

        {/* FAQ Section */}
        <Container maxW="5xl" py={{ base: 16, md: 24 }}>
          <VStack gap={12}>
            <Heading size="2xl" textAlign="center" color="gray.900">Frequently Asked Questions</Heading>
            <VStack align="stretch" gap={10} w="full">
              {[
                {
                  q: "Do I have to pay a subscription?",
                  a: "No. You only pay for impression-based visibility packs when you activate them. No monthly fees, no subscriptions."
                },
                {
                  q: "Can I activate multiple packs?",
                  a: "Yes! You can activate as many packs as you need, whenever you need them. There's no limit and no subscription required."
                },
                {
                  q: "Do I need my own website?",
                  a: "No. Your Spotlight page acts as a high-converting landing page. You can use it as your primary website if you don't have one."
                }
              ].map((faq, i) => (
                <Box key={i} pb={8} borderBottom="1px solid" borderColor="gray.200" _last={{ borderBottom: "none", pb: 0 }}>
                  <Heading size="md" mb={4} color="gray.900" fontWeight="semibold">
                    {faq.q}
                  </Heading>
                  <Text color="gray.600" fontSize="md" lineHeight="tall">
                    {faq.a}
                  </Text>
                </Box>
              ))}
            </VStack>
          </VStack>
        </Container>

        {/* Bottom CTA */}
        <Box bg="teal.700" color="white" py={{ base: 20, md: 32 }} position="relative" overflow="hidden">
           <Box
            position="absolute"
            top="0"
            left="0"
            w="full"
            h="full"
            bgGradient="linear(to-br, teal.700, teal.800)"
            opacity={0.9}
            zIndex={0}
          />
          <Container maxW="4xl" position="relative" zIndex={1}>
            <VStack gap={8} textAlign="center">
              <Heading size={{ base: '3xl', md: '4xl' }} letterSpacing="tight">
                Ready to start getting clients?
              </Heading>
              <Text fontSize="xl" color="teal.100" maxW="2xl" lineHeight="tall">
                Get the professional presence and visibility you deserve, all in one place.
              </Text>
              <Button
                size="xl"
                bg="white"
                color="teal.700"
                fontWeight="bold"
                px={12}
                py={8}
                fontSize="xl"
                onClick={handleApply}
                _hover={{ transform: 'scale(1.05)', shadow: '2xl', bg: 'gray.100' }}
                transition="all 0.2s"
              >
                <HStack gap={3}>
                  <Text>Start Getting Clients</Text>
                  <Icon as={FiArrowRight} />
                </HStack>
              </Button>
              <Text fontSize="sm" color="teal.200">
                Pay only for visibility packs when you're ready to grow.
              </Text>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box 
        position="fixed" 
        bottom={0} 
        left={0} 
        right={0} 
        bg="white" 
        p={4} 
        borderTopWidth="1px" 
        borderColor="gray.200"
        shadow="xl" 
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button 
          w="full" 
          colorPalette="brand" 
          size="lg" 
          onClick={handleApply}
          fontWeight="bold"
        >
          <HStack gap={2}>
            <Text>Start Getting Clients</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}

export default PricingPage